import React, { useState, useEffect, useRef } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  Briefcase,
  Check,
  Building2,
  X,
  GraduationCap,
  Mail,
  Phone,
  MapPin,
  Award,
  Clock,
  FileText,
  Laptop,
  Globe,
  MessageSquare,
  Filter,
  RefreshCw,
  Pause,
  Tag,
  History,
  User2,
  Languages,
  School,
  BookOpen,
  ArrowLeft,
  ClipboardList,
  Plus,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  applicationQueryKeys,
  useApplicationsForPreScreen,
} from "@/hooks/queries/useApplication"; // Updated sample data with UK education examples
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ThumbButtons from "./ThumbButtons";
import { applicationService } from "@/services/applicationService";
import CandidateNotes from "./CandidateNotes";
import Dropdown from "@/components/ui/Dropdown";
import ApplicationView from "@/components/applications/ApplicationView";
import ApplicationStatusHistory from "@/components/applications/ApplicationStatusHistory";
import ApplicationHistory from "@/components/PreScreen/ApplicationHistory";
import StatusHistoryDialog from "@/components/PreScreen/StatusHistoryDialog";
import ApplicationTags from "@/components/PreScreen/ApplicationTags";
import ApplicationFields from "@/components/PreScreen/ApplicationFields";
import CandidateScore from "@/components/PreScreen/CandidateScore";
import CandidateCV from "@/components/PreScreen/CandidateCV";
import JobDetail from "@/components/PreScreen/JobDetail";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useQueryClient } from "@tanstack/react-query";
import { vacancyQueryKeys } from "@/hooks/queries/usevacancy";
import CurrentApplicationView from "@/components/PreScreen/CurrentApplicationView";
import Loader from "@/components/Loader";
import { batchSerice } from "@/services/batchService"; // Import the batch service
import DialogBox from "@/components/Common/DialogBox";
import { useVacancyStatusSettingsByVacancyId } from "@/hooks/queries/useVacancyStatusSettings";

function normalizeRequestedApplicationIds(filters) {
  const raw = filters?.application_id;
  if (raw == null || raw === "") return [];
  const ids = Array.isArray(raw) ? raw : [raw];
  return ids.map((id) => String(id)).filter(Boolean);
}

function normalizeSkippedLockedFromResponse(data) {
  const skipped =
    data?.skipped_locked ||
    data?.skipped_locked_applications ||
    data?.unavailable_locked ||
    data?.locked_skipped;

  if (!skipped) return null;

  if (Array.isArray(skipped.by_user) || Array.isArray(skipped.locked_by_user)) {
    const byUser = skipped.by_user || skipped.locked_by_user;
    const normalized = byUser.map((entry) => ({
      user_id: entry.user_id ?? entry.id,
      user_name:
        entry.user_name || entry.name || `User #${entry.user_id ?? entry.id}`,
      count: entry.count ?? entry.application_ids?.length ?? 0,
      application_ids: entry.application_ids || [],
    }));
    return {
      total:
        skipped.total ??
        normalized.reduce((sum, entry) => sum + (entry.count || 0), 0),
      byUser: normalized.filter((entry) => entry.count > 0),
    };
  }

  if (Array.isArray(skipped)) {
    const byUserMap = {};
    skipped.forEach((item) => {
      const lockedBy = item.locked_by || item.assigned_user || {};
      const userId =
        lockedBy.id ??
        lockedBy.user_id ??
        item.assigned_user_id ??
        item.locked_by_user_id ??
        "unknown";
      const userName =
        lockedBy.name ||
        lockedBy.user_name ||
        item.locked_by_name ||
        `User #${userId}`;
      if (!byUserMap[userId]) {
        byUserMap[userId] = {
          user_id: userId,
          user_name: userName,
          count: 0,
          application_ids: [],
        };
      }
      byUserMap[userId].count += 1;
      if (item.application_id != null) {
        byUserMap[userId].application_ids.push(item.application_id);
      }
    });
    return {
      total: skipped.length,
      byUser: Object.values(byUserMap),
    };
  }

  return null;
}

async function resolveSkippedLockedInfo({
  vacancyId,
  requestedIds,
  returnedApplications,
  apiData,
}) {
  const fromApi = normalizeSkippedLockedFromResponse(apiData);
  if (fromApi?.total > 0) return fromApi;

  if (!requestedIds.length) return null;

  const returnedSet = new Set(
    (returnedApplications || []).map((app) => String(app.id)),
  );
  const skippedIds = requestedIds.filter((id) => !returnedSet.has(String(id)));
  if (skippedIds.length === 0) return null;

  const skippedSet = new Set(skippedIds.map(String));

  try {
    const locks = await applicationService.getApplicationLocks(vacancyId);
    const byUserMap = {};

    (locks?.status_summary || []).forEach((summary) => {
      (summary.locked_by_user || []).forEach((entry) => {
        const ids = (entry.application_ids || []).map(String);
        const overlap = ids.filter((id) => skippedSet.has(id));
        if (overlap.length === 0) return;

        const key = entry.user_id ?? entry.user_name ?? "unknown";
        if (!byUserMap[key]) {
          byUserMap[key] = {
            user_id: entry.user_id,
            user_name:
              entry.user_name || entry.name || `User #${entry.user_id}`,
            count: 0,
            application_ids: [],
          };
        }
        byUserMap[key].count += overlap.length;
        byUserMap[key].application_ids.push(...overlap);
      });
    });

    const byUser = Object.values(byUserMap);
    if (byUser.length > 0) {
      return { total: skippedIds.length, byUser };
    }
  } catch (error) {
    console.error("Failed to resolve locked application owners:", error);
  }

  return { total: skippedIds.length, byUser: [] };
}

function PreScreenView() {
  const { vacancyId: selectedVacancyId } = useParams();
  const [searchParams] = useSearchParams();

  // Get all filters from search params
  const filters = {};

  // Get vacancy_status_id from search params if it exists
  const vacancyStatusId = searchParams.get("vacancy_status_id");
  console.log("Initial vacancy_status_id from URL:", vacancyStatusId);
  if (vacancyStatusId) {
    filters.vacancy_status_id = vacancyStatusId;
    console.log("Applied vacancy_status_id filter:", filters);
  }

  // Get all other filters from URL parameters
  console.log("searchParams", searchParams.toString());
  // const customFilters = {};
  for (const [key, value] of searchParams.entries()) {
    if (key !== "vacancy_status_id") {
      // abc+xy to abc xy .. its coming from the query string.. make it work for all props
      // revers all url encoded characters
      const filterKey = decodeURIComponent(key).replace("+", " ");
      const filterValue = decodeURIComponent(value);
      console.log("filterKey", filterKey);
      console.log("filterValue", filterValue);

      // Map filter names to match backend expectations
      //let filterKey = key;
      // if (key === 'stage') {
      //   filterKey = 'vacancy_status_id';
      // }
      // if key is already in the filters object, make it array with old value merged.. else kepp it key value pair
      // it can be mixed as {test: 'test', test2: ['test2', 'test3']}
      if (filters[filterKey]) {
        if (Array.isArray(filters[filterKey])) {
          filters[filterKey].push(filterValue);
        } else {
          filters[filterKey] = [filters[filterKey], filterValue];
        }
      } else {
        filters[filterKey] = filterValue;
      }
      console.log("Filters check:", filters?.applications);

      // Handle multiple values for the same key (for multi-select filters)
      // if (filters[filterKey]) {
      //   if (Array.isArray(filters[filterKey])) {
      //     filters[filterKey].push(value);
      //   } else {
      //     filters[filterKey] = [filters[filterKey], value];
      //   }
      // } else {
      //   filters[filterKey] = value;
      // }
    }
  }

  console.log("All filters from URL:", filters);
  console.log("Vacancy ID:", selectedVacancyId);

  const { data: applications, isLoading } = useApplicationsForPreScreen(
    selectedVacancyId,
    filters,
  );
  const [availableTags, setAvailableTags] = useState([]);
  const [error, setError] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [skippedLockedInfo, setSkippedLockedInfo] = useState(null);
  const skippedLockedResolvedRef = useRef(false);
  const queryClient = useQueryClient();
  const { showToast } = useToastStore;

  // We'll handle the dynamic API call in the internal component

  // Manual API trigger as fallback - trigger immediately when component mounts
  useEffect(() => {
    const triggerVacancyStatusSettingsAPI = async () => {
      if (selectedVacancyId) {
        console.log(
          "Component mounted - API will only trigger when application is opened",
        );
        // API will only be called when currentApplication is available
      }
    };

    // Just log that component is mounted, don't trigger API calls
    triggerVacancyStatusSettingsAPI();
  }, [selectedVacancyId]);

  //get all tags api
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const { data: response } = await applicationService.AllTags();
        setAvailableTags(response.tags || []);
      } catch (err) {
        setError(err.message || "Failed to fetch tags");
        setShowErrorDialog(true);
      }
    };
    fetchAllTags();
  }, []);

  // Add error handling for applications data
  useEffect(() => {
    if (applications?.error) {
      setError(applications.error);
      setShowErrorDialog(true);
    }
  }, [applications]);

  // Detect when some selected applications were skipped because another recruiter locked them
  useEffect(() => {
    if (
      !applications ||
      applications?.error ||
      skippedLockedResolvedRef.current
    ) {
      return;
    }

    let cancelled = false;

    const checkSkippedLocked = async () => {
      const info = await resolveSkippedLockedInfo({
        vacancyId: selectedVacancyId,
        requestedIds: normalizeRequestedApplicationIds(filters),
        returnedApplications: applications?.applications || [],
        apiData: applications,
      });

      if (cancelled) return;

      skippedLockedResolvedRef.current = true;
      if (!info?.total) {
        setSkippedLockedInfo(null);
        return;
      }

      setSkippedLockedInfo({
        ...info,
        availableCount: applications?.applications?.length || 0,
      });
    };

    checkSkippedLocked();

    return () => {
      cancelled = true;
    };
    // filters is rebuilt each render from URL; application_id is the only input that matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, selectedVacancyId, searchParams]);

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );

  return (
    <PreScreenViewinternal
      batch={applications?.batch}
      applications={applications?.applications || []}
      vacancy={applications?.vacancy}
      availableTags={availableTags}
      vacancyId={selectedVacancyId}
      skippedLockedInfo={skippedLockedInfo}
    />
  );
}

function SkippedLockedBanner({ info, onDismiss }) {
  if (!info?.total) return null;

  const recruiterLabel =
    info.byUser?.length === 1
      ? info.byUser[0].user_name
      : info.byUser?.length > 1
        ? `${info.byUser.length} recruiters`
        : "another recruiter";

  return (
    <div className="w-full max-w-7xl mb-4 flex justify-center">
      <div className="skipped-locked-banner relative inline-flex max-w-full items-center gap-2.5 overflow-hidden rounded-xl border border-amber-300/90 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 px-4 py-2.5 pl-4">
        <div className="skipped-locked-banner__glow pointer-events-none absolute -inset-1 rounded-xl bg-gradient-to-r from-amber-300/40 via-orange-300/30 to-amber-300/40 blur-md" />
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
          <div className="skipped-locked-banner__shimmer absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/55 to-transparent" />
        </div>
        {/* <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-400 via-orange-400 to-amber-500" /> */}

        {/* <div className="skipped-locked-banner__icon relative z-[1] flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 shadow-sm ring-2 ring-amber-300/60">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/40" />
          <AlertCircle className="relative h-3.5 w-3.5" />
        </div> */}

        <p className="relative z-[1] whitespace-nowrap text-sm text-amber-900/85">
          <span className="font-semibold tracking-tight text-amber-950">
            {info.total} candidate{info.total === 1 ? "" : "s"} already opened by{" "}
            {recruiterLabel}.
          </span>{" "}
          Continuing with{" "}
          <span className="font-semibold text-amber-950">
            {info.availableCount || 0}
          </span>{" "}
          available for review.
          {info.byUser?.length > 1 && (
            <span className="ml-2 inline-flex flex-wrap gap-1.5 align-middle">
              {info.byUser.map((entry) => (
                <span
                  key={entry.user_id || entry.user_name}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300/80 bg-white/90 px-2 py-0.5 text-xs font-medium text-amber-950 shadow-sm"
                >
                  <User2 className="h-3 w-3 text-amber-600" />
                  {entry.user_name}
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 tabular-nums font-semibold text-amber-800">
                    {entry.count}
                  </span>
                </span>
              ))}
            </span>
          )}
        </p>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="relative z-[1] shrink-0 rounded-md p-1 text-amber-700/70 transition-all hover:scale-110 hover:bg-amber-100 hover:text-amber-900"
            aria-label="Dismiss notice"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function PreScreenViewinternal({
  batch,
  applications: Applications,
  vacancy: Vacancy,
  availableTags,
  vacancyId,
  selectedApplicant,
  skippedLockedInfo = null,
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { vacancyId: selectedVacancyId } = useParams();
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [currentVacancy, setCurrentVacancy] = useState(Vacancy);
  const [newTag, setNewTag] = useState("");
  const navigate = useNavigate();
  const [applications, setApplications] = useState(Applications);
  const [savedTags, setSavedTags] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState("");
  const [showCV, setShowCV] = useState(true);
  const [applicationsState, setApplicationsState] = useState(Applications);
  const [decisions, setDecisions] = useState({});
  const [tagsPerApplication, setTagsPerApplication] = useState({});
  const [scoresPerApplication, setScoresPerApplication] = useState({});
  const [applicationsStatus, setApplicationsStatus] = useState({});
  const [notesPerApplication, setNotesPerApplication] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [decisionHistory, setDecisionHistory] = useState([]);
  const queryClient = useQueryClient();
  const [isDecsionPending, setIsDecsionPending] = useState(false);
  const currentApplication = applicationsState[currentIndex];
  const isReviewComplete = currentIndex >= applicationsState?.length;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState(null);
  const { showToast } = useToastStore;
  const [isSkippedLockedBannerDismissed, setIsSkippedLockedBannerDismissed] =
    useState(false);

  const [showApplicationViewModal, setShowApplicationViewModal] =
    useState(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isClosingAfterSubmit, setIsClosingAfterSubmit] = useState(false);
  const allowUnloadRef = useRef(false);
  const [extraSettingsData, setExtraSettingsData] = useState(null);
  const [currentApplicationStatusId, setCurrentApplicationStatusId] =
    useState(null);
  const latestProgressRef = useRef({
    currentIndex: 0,
    totalApplications: 0,
    decisionCount: 0,
    reviewedCount: 0,
  });

  // useEffect(() => {
  //   console.log("Applications", applications);
  // }, [applications]);

  // Track current application's status ID
  useEffect(() => {
    if (currentApplication) {
      const statusId = currentApplication.vacancy_status_id;
      console.log("Current application status ID:", statusId);
      setCurrentApplicationStatusId(statusId);
    }
  }, [currentApplication]);

  useEffect(() => {
    latestProgressRef.current = {
      currentIndex,
      totalApplications: applicationsState?.length || 0,
      decisionCount: Object.keys(decisions).length,
      reviewedCount: currentIndex,
    };
  }, [currentIndex, applicationsState?.length, decisions]);

  // Dynamic API call for vacancy status settings based on current application status
  // Only call API when there's a current application
  const {
    data: vacancyStatusSettings,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = useVacancyStatusSettingsByVacancyId(
    vacancyId,
    currentApplicationStatusId,
    !!currentApplication, // Only enable when there's a current application
  );

  // Log API call status
  useEffect(() => {
    console.log("Dynamic Vacancy Status Settings API Status:", {
      isLoading: isLoadingSettings,
      hasData: !!vacancyStatusSettings,
      error: settingsError,
      vacancyId: vacancyId,
      currentApplicationStatusId: currentApplicationStatusId,
      currentApplication: currentApplication?.id,
      currentApplicationStatus: currentApplication?.vacancy_status_id,
    });
  }, [
    isLoadingSettings,
    vacancyStatusSettings,
    settingsError,
    vacancyId,
    currentApplicationStatusId,
    currentApplication,
  ]);

  // Manual API trigger for current application status - only when application is open
  useEffect(() => {
    const fetchCurrentStatusSettings = async () => {
      if (vacancyId && currentApplicationStatusId && currentApplication) {
        console.log(
          `Manually fetching settings for current application status ID: ${currentApplicationStatusId}`,
        );
        try {
          const { vacancyStatusSettingsService } =
            await import("@/services/vacancyStatusSettingsService");
          const result =
            await vacancyStatusSettingsService.getVacancyStatusSettingsByVacancyId(
              vacancyId,
              currentApplicationStatusId,
            );
          console.log(
            `Settings for current application status (ID: ${currentApplicationStatusId}):`,
            result,
          );
        } catch (error) {
          console.error(
            `Failed to fetch settings for status ID ${currentApplicationStatusId}:`,
            error,
          );
        }
      }
    };

    // Only trigger when there's a current application and status ID
    if (currentApplicationStatusId && currentApplication) {
      fetchCurrentStatusSettings();
    }
  }, [vacancyId, currentApplicationStatusId, currentApplication]);

  // Function to manually test different status IDs
  const testStatusSettings = async (statusId) => {
    if (vacancyId && statusId) {
      console.log(`Testing settings for status ID: ${statusId}`);
      try {
        const { vacancyStatusSettingsService } =
          await import("@/services/vacancyStatusSettingsService");
        const result =
          await vacancyStatusSettingsService.getVacancyStatusSettingsByVacancyId(
            vacancyId,
            statusId,
          );
        console.log(`Test result for status ID ${statusId}:`, result);
        return result;
      } catch (error) {
        console.error(`Test failed for status ID ${statusId}:`, error);
        return null;
      }
    }
  };

  // Expose test function globally
  useEffect(() => {
    window.testStatusSettings = testStatusSettings;
    return () => {
      delete window.testStatusSettings;
    };
  }, [vacancyId]);

  // Log vacancy status settings when available and store extra settings
  useEffect(() => {
    if (vacancyStatusSettings) {
      console.log("Vacancy Status Settings:", vacancyStatusSettings);
      console.log(
        "Extra Settings Data:",
        vacancyStatusSettings?.extra_settings,
      );

      // Store extra settings data for use throughout the component
      if (vacancyStatusSettings?.extra_settings) {
        setExtraSettingsData(vacancyStatusSettings.extra_settings);
        console.log(
          "Extra settings data stored:",
          vacancyStatusSettings.extra_settings,
        );
      }
    }
  }, [vacancyStatusSettings]);

  // Log when extra settings data changes
  useEffect(() => {
    if (extraSettingsData) {
      console.log("Extra settings data updated:", extraSettingsData);
    }
  }, [extraSettingsData]);

  useEffect(() => {
    const fetchBatchData = async () => {
      if (batch?.id) {
        try {
          const progressState = latestProgressRef.current;
          const reviewedPendingCount = progressState.decisionCount;
          // Keep true while reviewed apps exist that are not yet updated/submitted.
          const hasProgress = reviewedPendingCount > 0;

          const heartbeatPayload = {
            has_progress: hasProgress,
          };

          const response = await batchSerice.getBatches(
            batch?.id,
            heartbeatPayload,
          );

          if (response.error) {
            console.log("response", response);
            setErrorMessage(
              "Your batch has expired because no action was taken for 10 minutes. Please reload the page to continue. To prevent this, make sure to take an action within 10 minutes.",
            );
            setShowErrorDialog(true);
          }
        } catch (error) {
          setErrorMessage(
            "Your batch has expired because no action was taken for 10 minutes. Please reload the page to continue. To prevent this, make sure to take an action within 10 minutes.",
          );
          setShowErrorDialog(true);
        }
      }
    };

    fetchBatchData();

    // let interval = setInterval(fetchBatchData, (1000 * 60 * 2)); // call every 2 minutes
    let interval = setInterval(fetchBatchData, 1000 * 60 * 2);

    return () => clearInterval(interval);
  }, [batch]);

  // state for filters
  const [filters, setFilters] = useState({});
  const [showNoMatchModal, setShowNoMatchModal] = useState(false);

  const getFilterOptions = () => {
    const filterOptions = {};

    Applications.forEach((app) => {
      const personalFields = app.application_fields || [];
      personalFields.forEach((field) => {
        if (field.field_value && field.form_type == "personal") {
          filterOptions[field.field_name] =
            filterOptions[field.field_name] || new Set();

          // Special handling for languages field
          if (field.field_name.toLowerCase().includes("language")) {
            // Split combined languages and add each individually
            const languages = field.field_value.split(/[\s,]+/);
            languages.forEach((lang) => {
              if (lang.trim()) {
                filterOptions[field.field_name].add(lang.trim());
              }
            });
          } else {
            // Handle other fields normally
            filterOptions[field.field_name].add(field.field_value);
          }
        }
      });
    });

    return Object.entries(filterOptions).reduce((acc, [key, values]) => {
      acc[key] = Array.from(values).filter(Boolean);
      return acc;
    }, {});
  };

  //get display name for filter keys
  const getDisplayName = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  //Apply filters
  const filterApplications = () => {
    return Applications.filter((app) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;

        const field = app.application_fields?.find((f) => f.field_name === key);
        if (!field) return false;

        // Special handling for languages field
        if (key.toLowerCase().includes("language")) {
          const applicantLanguages = field.field_value
            .split(/[\s,]+/)
            .map((l) => l.trim());
          return applicantLanguages.includes(value);
        }

        return field.field_value === value;
      });
    });
  };

  // Update applications when filters change
  useEffect(() => {
    const filtered = filterApplications();
    setApplicationsState(filtered);
    setCurrentIndex(0);

    // Clear decision history when filters change to avoid confusion
    setDecisionHistory([]);

    // Handle no matches in a separate effect to avoid infinite loops
    if (filtered.length === 0 && Object.keys(filters).length > 0) {
      setShowNoMatchModal(true);
      const lastFilterKey =
        Object.keys(filters)[Object.keys(filters).length - 1];
      const { [lastFilterKey]: removed, ...remainingFilters } = filters;

      // Use setTimeout to break the render cycle
      setTimeout(() => {
        setFilters(remainingFilters);
      }, 0);
    }
  }, [filters, Applications]);

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({});
    setApplicationsState(Applications);
    setCurrentIndex(0);
  };

  // Get filter options
  const filterOptions = getFilterOptions();

  useEffect(() => {
    if (currentApplication) {
      setTagsPerApplication((prev) => {
        if (!prev[currentApplication.id]) {
          return {
            ...prev,
            [currentApplication.id]:
              currentApplication?.application_tags?.map((tag) => tag.name) ??
              [],
          };
        }
        return prev;
      });

      // Initialize scores correctly from application_score_cards
      setScoresPerApplication((prev) => {
        if (!prev[currentApplication.id]) {
          // Map existing scores to the correct format from application_score_cards
          const initialScores =
            currentApplication?.application_score_cards?.map((score) => ({
              vacancy_scorecard_id: score.vacancy_scorecard_id,
              score: score.score,
            })) || [];

          return {
            ...prev,
            [currentApplication.id]: initialScores,
          };
        }
        return prev;
      });
    }
  }, [currentApplication]);

  // Track unsaved changes
  useEffect(() => {
    if (allowUnloadRef.current || isClosingAfterSubmit) {
      setHasUnsavedChanges(false);
      return;
    }
    // Consider changes unsaved if we have any decisions, notes, scores, tags or status changes
    const unsaved =
      Object.keys(decisions).length > 0 ||
      Object.keys(notesPerApplication).length > 0 ||
      Object.keys(scoresPerApplication).length > 0 ||
      Object.keys(tagsPerApplication).length > 0 ||
      Object.keys(applicationsStatus).length > 0;

    setHasUnsavedChanges(unsaved);
  }, [
    decisions,
    notesPerApplication,
    scoresPerApplication,
    tagsPerApplication,
    applicationsStatus,
    isClosingAfterSubmit,
  ]);

  // Setup beforeunload event handler
  const handleBeforeUnload = (e) => {
    if (allowUnloadRef.current || isClosingAfterSubmit) {
      return;
    }
    if (hasUnsavedChanges) {
      // Standard way to show a confirmation dialog when leaving
      e.preventDefault();
      e.returnValue = ""; //'You have unsaved changes. Are you sure you want to leave?';
      // return e.returnValue;
    }
  };
  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, isClosingAfterSubmit]);

  const handleAddTag = async (application_id, tags) => {
    if (tags.trim()) {
      const newTagsArray = tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const existingTags = tagsPerApplication[application_id] || [];
      const updatedTags = [...existingTags, ...newTagsArray];

      // Update local state
      setTagsPerApplication((prev) => ({
        ...prev,
        [application_id]: updatedTags,
      }));

      // Immediately store tags
      try {
        const mappedTags = {
          applications: [
            {
              application_id: parseInt(application_id),
              tag_names: updatedTags,
            },
          ],
        };

        await applicationService.BulkTagsUpdate(mappedTags);
        showToast({
          message: "Tags updated successfully",
          type: "success",
        });
        queryClient.invalidateQueries({
          queryKey: [applicationQueryKeys.details(selectedVacancyId)],
        });
      } catch (error) {
        setError(error.message || "Failed to update tags");
        setShowErrorDialog(true);
        // Rollback local state on error
        setTagsPerApplication((prev) => ({
          ...prev,
          [application_id]: existingTags,
        }));
      }

      setNewTag(null);
    }
  };

  const handleRemoveTag = async (indexToRemove) => {
    const existingTags = tagsPerApplication[currentApplication.id] || [];
    const updatedTags = existingTags.filter(
      (_, index) => index !== indexToRemove,
    );

    // Update local state
    setTagsPerApplication((prev) => ({
      ...prev,
      [currentApplication.id]: updatedTags,
    }));

    // Immediately store updated tags
    try {
      const mappedTags = {
        applications: [
          {
            application_id: parseInt(currentApplication.id),
            tag_names: updatedTags,
          },
        ],
      };

      await applicationService.BulkTagsUpdate(mappedTags);
      showToast({
        message: "Tags updated successfully",
        type: "success",
      });
      queryClient.invalidateQueries({
        queryKey: [applicationQueryKeys.details(selectedVacancyId)],
      });
    } catch (error) {
      console.error("Failed to update tags:", error);
      setError(error.message || "Failed to update tags");
      setShowErrorDialog(true);

      setTagsPerApplication((prev) => ({
        ...prev,
        [currentApplication.id]: existingTags,
      }));
    }
  };

  const handleScoreChange = (applicationId, scores, totalScore) => {
    setScoresPerApplication((prev) => ({
      ...prev,
      [applicationId]: scores,
    }));

    // Update application state with new total score
    setApplicationsState((apps) =>
      apps.map((app) =>
        app.id === applicationId
          ? {
              ...app,
              total_score: {
                total_score: totalScore.toFixed(2),
                score_details: scores,
              },
            }
          : app,
      ),
    );

    // Immediately store scores in the API
    const storeScores = async () => {
      const mappedScores = {
        applications: [
          {
            application_id: parseInt(applicationId),
            scores: scores.map((score) => ({
              vacancy_score_id: score.vacancy_scorecard_id,
              score: score.score,
            })),
          },
        ],
      };

      try {
        await applicationService.BulkScoreCardsUpdate(mappedScores);

        queryClient.invalidateQueries({
          queryKey: [applicationQueryKeys.details(selectedVacancyId)],
        });
      } catch (error) {
        console.error("Failed to update scores:", error);
        setError(error.message || "Failed to update scores");
        setShowErrorDialog(true);
      }
    };

    storeScores();
  };

  // Helper function to get target status based on decision and extra settings
  const getTargetStatusForDecision = (decision) => {
    const extraSettings = vacancyStatusSettings?.data?.extra_settings;
    const prescreenSettings = extraSettings?.prescreen_setting;

    console.log("Getting target status for decision:", {
      decision,
      extraSettings,
      prescreenSettings,
      acceptStatus: prescreenSettings?.accept,
      rejectStatus: prescreenSettings?.reject,
    });

    if (decision === "accepted") {
      // Check if custom accept status is defined
      if (prescreenSettings?.accept?.id) {
        console.log("Using custom accept status:", prescreenSettings.accept);
        return prescreenSettings.accept.id;
      }

      // Fallback to next status logic
      console.log("No custom accept status, finding next status");
      const currentStatus =
        currentVacancy?.vacancy_statuses?.find(
          (status) => status.id === currentApplication.vacancy_status_id,
        ) || currentVacancy?.vacancy_statuses?.[0];

      const currentOrder = parseInt(currentStatus?.p_order) || 0;
      const availableOrders = currentVacancy?.vacancy_statuses
        ?.map((status) => parseInt(status.p_order))
        .sort((a, b) => a - b);

      const nextOrder = availableOrders?.find((order) => order > currentOrder);
      const nextStatus = currentVacancy?.vacancy_statuses?.find(
        (status) => parseInt(status.p_order) === nextOrder,
      );

      return nextStatus?.id || null;
    } else if (decision === "rejected") {
      // Check if custom reject status is defined
      if (prescreenSettings?.reject?.id) {
        console.log("Using custom reject status:", prescreenSettings.reject);
        return prescreenSettings.reject.id;
      }

      // Fallback to last status logic
      console.log("No custom reject status, finding last status");
      const lastStatus = currentVacancy?.vacancy_statuses?.reduce(
        (prev, curr) =>
          parseInt(curr.p_order) > parseInt(prev.p_order) ? curr : prev,
      );

      return lastStatus?.id || null;
    }

    return null;
  };

  const handleDecision = (decision) => {
    if (isDecsionPending) return;

    {
      setDirection(
        decision === "accepted"
          ? "right"
          : decision === "rejected"
            ? "left"
            : "none",
      );

      // Get target status based on decision and extra settings
      const targetStatusId = getTargetStatusForDecision(decision);

      console.log("Decision made:", {
        decision,
        targetStatusId,
        currentApplication: currentApplication?.id,
        currentStatus: currentApplication?.vacancy_status_id,
      });

      // Update application status if target status is found
      if (
        targetStatusId &&
        (decision === "accepted" || decision === "rejected")
      ) {
        setApplicationsStatus((prev) => {
          console.log(
            `Moving application ${currentApplication.id} to ${decision} status:`,
            targetStatusId,
          );
          return {
            ...prev,
            [currentApplication.id]: targetStatusId,
          };
        });
      } else {
        console.log(`Warning: No target status found for ${decision} decision`);
      }

      // Update decisions
      setDecisions((prev) => {
        const updatedDecisions = {
          ...prev,
          [currentApplication.id]: decision,
        };
        // Get current application from applicationsState
        const thisApplication = applicationsState
          ? applicationsState[currentIndex]
          : null;

        // Capture current scores and notes state before adding to history
        const currentScores = scoresPerApplication[currentApplication.id];
        const currentNotes = notesPerApplication[currentApplication.id];
        const currentTags = tagsPerApplication[currentApplication.id];

        // Add to decision history for undo functionality with scores and notes
        setDecisionHistory((prevHistory) => [
          ...prevHistory,
          {
            applicationId: currentApplication.id,
            decision: decision,
            previousIndex: currentIndex,
            timestamp: Date.now(),
            scores: currentScores,
            notes: thisApplication?.application_notes || currentNotes,
            tags: currentTags,
          },
        ]);

        // Calculate and show notification with current counts
        const counts = Object.values(updatedDecisions).reduce(
          (acc, dec) => {
            acc[dec] = (acc[dec] || 0) + 1;
            return acc;
          },
          { accepted: 0, rejected: 0, hold: 0 },
        );

        // Set notification state instead of using toast
        setNotification(counts);
        setShowNotification(true);

        return updatedDecisions;
      });
    }
    setIsDecsionPending(true);
    // Move to next application after animation
    setTimeout(() => {
      setDirection("");
      setIsDecsionPending(false);
      setCurrentIndex((prev) => prev + 1);
    }, 300);
  };

  const handleUndo = () => {
    // Check if we can undo (have history and not at the first candidate)
    if (decisionHistory.length === 0) {
      showToast({
        message: "No decisions to undo",
        type: "warning",
      });
      return;
    }

    if (currentIndex === 0) {
      showToast({
        message: "Already at the first candidate",
        type: "warning",
      });
      return;
    }

    const lastDecision = decisionHistory[decisionHistory.length - 1];

    // Remove the last decision from history
    setDecisionHistory((prev) => prev.slice(0, -1));

    // Remove the decision from the decisions state
    setDecisions((prev) => {
      const updated = { ...prev };
      delete updated[lastDecision.applicationId];
      return updated;
    });

    // Remove the status change if it exists
    setApplicationsStatus((prev) => {
      const updated = { ...prev };
      delete updated[lastDecision.applicationId];
      return updated;
    });

    // Restore scores from history if they were preserved
    if (lastDecision.scores !== undefined) {
      setScoresPerApplication((prev) => ({
        ...prev,
        [lastDecision.applicationId]: lastDecision.scores,
      }));
    }

    // Restore notes from history if they were preserved
    if (lastDecision.notes !== undefined) {
      setNotesPerApplication((prev) => ({
        ...prev,
        [lastDecision.applicationId]: lastDecision.notes,
      }));
    }

    // Restore tags from history if they were preserved
    if (lastDecision.tags !== undefined) {
      setTagsPerApplication((prev) => ({
        ...prev,
        [lastDecision.applicationId]: lastDecision.tags,
      }));
    }

    // Move back to the previous application
    setCurrentIndex(lastDecision.previousIndex);

    // Show toast notification
    // showToast({
    //   message: `Undid ${lastDecision.decision} decision for previous candidate`,
    //   type: "info"
    // });
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (isReviewComplete) return;

      // Handle undo with Ctrl+Z
      if (event.ctrlKey && event.key === "z") {
        event.preventDefault();
        handleUndo();
        return;
      }

      if (event.key === "ArrowLeft") {
        handleDecision("rejected");
      } else if (event.key === "ArrowRight") {
        handleDecision("accepted");
      } else if (event.key === "ArrowDown") {
        handleDecision("hold");
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isReviewComplete, currentApplication, decisionHistory]);

  const getSummary = () => {
    const summary = {
      accepted: 0,
      rejected: 0,
      hold: 0,
      total: 0,
    };

    Object.values(decisions).forEach((decision) => {
      summary[decision]++;
      summary.total++;
    });

    return summary;
  };

  const storeStatuses = async () => {
    console.log("Current applicationsStatus state:", applicationsStatus);
    console.log("Current decisions state:", decisions);

    // Get hold application IDs as simple array
    const holdApplicationIds = Object.entries(decisions)
      .filter(([applicationId, decision]) => decision === "hold")
      .map(([applicationId]) => parseInt(applicationId));

    // Get applications with status changes (accepted/rejected)
    const applicationsWithStatusChanges = Object.entries(
      applicationsStatus,
    ).map(([applicationId, statusId]) => {
      const mappedStatus = {
        id: parseInt(applicationId),
        vacancy_status_id: statusId,
      };
      console.log("Mapping application status:", mappedStatus);
      return mappedStatus;
    });

    const payload = {
      hold: holdApplicationIds,
      applications: applicationsWithStatusChanges,
    };

    console.log("Final payload to be sent:", payload);

    // Only send if there are hold applications or applications with status changes
    if (
      holdApplicationIds.length > 0 ||
      applicationsWithStatusChanges.length > 0
    ) {
      await applicationService
        .updateApplicationsStatus(payload)
        .then((response) => {
          // console.log('Statuses updated successfully:', response);
        })
        .catch((error) => {
          console.error("Failed to update statuses:", error);
        });
    }
  };

  const storeNotes = async () => {
    const mappedNotes = {
      applications: Object.entries(notesPerApplication).map(
        ([applicationId, notes]) => ({
          application_id: parseInt(applicationId),
          comments: notes.map((note) => {
            const commentObj = {
              comment: typeof note === "object" && note.note ? note.note : note,
            };
            if (typeof note === "object" && note.note_id) {
              commentObj.comment_id = note.note_id;
            }
            return commentObj;
          }),
        }),
      ),
    };

    // console.log(mappedNotes);
    // return;

    if (mappedNotes.applications.length > 0) {
      await applicationService
        .BulkNotesUpdate(mappedNotes)
        .then((response) => {
          // console.log('Notes updated successfully:', response);
        })
        .catch((error) => {
          console.error("Failed to update notes:", error);
        });
    }
  };

  const submitApplications = async () => {
    setIsSubmitting(true);
    try {
      await Promise.all([
        // storeScores(),
        storeStatuses(),
      ]);

      queryClient.invalidateQueries({
        queryKey: [applicationQueryKeys.details(selectedVacancyId)],
      });
      queryClient.invalidateQueries({
        queryKey: [vacancyQueryKeys.details(selectedVacancyId)],
      });

      showToast({
        message: "Applications submitted successfully",
        type: "success",
        duration: 3000,
      });

      // Prevent leave-site alert + "Other recruiters" empty screen while closing.
      allowUnloadRef.current = true;
      setIsClosingAfterSubmit(true);
      setHasUnsavedChanges(false);
      setNotesPerApplication({});
      setScoresPerApplication({});
      setTagsPerApplication({});
      setDecisionHistory([]);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Keep decisions until close so summary/empty recruiters UI does not flash.
      setTimeout(() => {
        window.close();
      }, 3000);
    } catch (error) {
      setError(error.message || "Failed to submit applications,Please try again.");
      setShowErrorDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const cvUrl = currentApplication?.resume;

  const handleAddNote = (applicationId, note) => {
    // Update application state with new note
    const thisApplication = applicationsState
      ? applicationsState[currentIndex]
      : null;
    if (thisApplication) {
      thisApplication.application_notes = [
        ...(thisApplication.application_notes || []),
        note,
      ];
    }

    setNotesPerApplication((prev) => ({
      ...prev,
      [applicationId]: [...(prev[applicationId] || []), note],
    }));
  };
  const handleEditNote = (applicationId, note, old_index) => {
    // Extra mile: Update application state with new note
    const thisApplication = applicationsState
      ? applicationsState[currentIndex]
      : null;
    if (thisApplication) {
      note.id = old_index;
      thisApplication.application_notes =
        thisApplication.application_notes?.map((existingNote, index) =>
          existingNote.id === note.id
            ? { ...existingNote, comment: note?.comment || "" }
            : existingNote,
        );
    }

    setNotesPerApplication((prev) => ({
      ...prev,
      [applicationId]: prev[applicationId]?.map((existingNote, index) =>
        index === old_index ? note : existingNote,
      ),
    }));
  };

  // History Modal State
  const [selectedPreviousApplication, setselectedPreviousApplication] =
    useState(null);

  const handleApplicationHistoryModalOpen = (applicant) => {
    setselectedPreviousApplication(applicant);
  };

  const handleApplicationHistoryModalClose = () => {
    setselectedPreviousApplication(null);
  };

  const [notification, setNotification] = useState(null);
  const [showNotification, setShowNotification] = useState(false);

  // Custom Notification Component
  const NotificationPopup = ({ className = "" }) => {
    if (!showNotification || !notification) return null;

    // Calculate total reviewed applications and percentage
    const total =
      notification.accepted + notification.rejected + notification.hold;
    const totalApplications = applications.length;
    const reviewedPercentage = Math.round((total / totalApplications) * 100);

    const handleSubmit = async () => {
      try {
        setIsSubmitting(true);
        await submitApplications();
        setShowNotification(false);
      } catch (error) {
        console.error("Failed to submit applications:", error);
        showToast({
          message: "Failed to submit applications",
          type: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleCancelClick = () => {
      setShowCancelConfirm(true);
    };

    return (
      // <div className="fixed bottom-6 right-32 mr-8 w-full max-w-[290px]">
      <div className={`bg-card rounded-lg shadow-md p-5 ${className}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground">
              Total Applications Reviewed
            </h3>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="text-4xl font-bold text-primary-dark">
            {total}{" "}
            <span className="text-muted-foreground text-xl font-normal">
              / {totalApplications}
            </span>
          </div>

          <div className="relative h-16 w-16">
            <svg className="w-full h-full" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                stroke="#e6e6e6"
                strokeWidth="2"
              />
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                stroke="currentColor"
                className="text-primary"
                strokeWidth="2"
                strokeDasharray={`${reviewedPercentage} 100`}
                strokeLinecap="round"
                transform="rotate(-90 18 18)"
              />
              <text
                x="18"
                y="18"
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize="8"
                fontWeight="bold"
                fill="#primary"
              >
                {reviewedPercentage}%
              </text>
            </svg>
          </div>
        </div>

        <div className="flex justify-between gap-2 mt-4">
          {/* <button 
              onClick={handleCancelClick}
              className="flex-1 px-3 py-2 border border-input text-foreground rounded text-sm font-medium"
            >
              Cancel
            </button> */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            //i want  on hover zoom in the button
            className="flex-1 px-3 py-2 bg-primary-dark hover:bg-primary-dark hover:scale-105 transition-all duration-300 text-white rounded text-sm font-medium"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="w-3 h-3 mr-1 inline animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
      // </div>
    );
  };

  // Confirmation Modal Component
  const CancelConfirmationModal = () => {
    if (!showCancelConfirm) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg shadow-lg p-4 max-w-sm w-full mx-4">
          <div className="flex flex-col gap-3">
            <h3 className="font-semibold text-lg text-foreground">
              Confirm Cancel
            </h3>
            <p className="text-muted-foreground">
              Are you sure you want to cancel? This will discard all your
              current application decisions.
            </p>
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-3 py-1.5 bg-muted hover:bg-input text-foreground rounded text-sm font-medium"
              >
                No
              </button>
              <button
                onClick={() => {
                  // Clear decisions and reset state
                  setDecisions({});
                  setApplicationsStatus({});
                  setShowNotification(false);
                  // Refresh the current view
                  setApplicationsState([...applications]);
                  setCurrentIndex(0);
                  setShowCancelConfirm(false);
                }}
                className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isClosingAfterSubmit) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <div className="p-5 rounded-lg flex flex-col items-center gap-3">
          <Loader2 className="w-12 h-12 animate-spin text-primary-dark" />
          <p className="text-dark text-sm">Closing...</p>
        </div>
      </div>
    );
  }

  if (isReviewComplete) {
    const summary = getSummary();
    if (
      summary.accepted === 0 &&
      summary.rejected === 0 &&
      summary.hold === 0
    ) {
      return (
        <div className="min-h-screen bg-muted flex items-center justify-center">
          <div className="max-w-2xl w-full px-4">
            <div className="bg-card rounded-xl shadow-xl px-8 py-6 text-center mx-auto">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Other recruiters are currently reviewing all available
                applications.
              </h3>
              <p className="text-muted-foreground text-base mb-4">
                If nobody else is actively reviewing, this message may appear
                because another recruiter has a review tab open in the
                background.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    navigate(-1);
                    // Close the tab after a short delay to allow navigation
                    setTimeout(() => {
                      window.close();
                    }, 1000);
                  }}
                  className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-muted flex flex-col items-center justify-center p-4">
        <div className="space-y-6 w-full max-w-2xl">
          <div className="bg-card rounded-xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Application Review Summary
            </h2>
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-100 p-6 rounded-lg text-center">
                  <div className="text-4xl font-bold text-red-700 mb-2">
                    {summary.rejected}
                  </div>
                  <div className="text-red-600 font-medium">
                    Rejected ({" "}
                    {summary.rejected > 0
                      ? Math.round((summary.rejected / summary.total) * 100)
                      : 0}
                    %)
                  </div>
                </div>

                <div className="bg-yellow-100 p-6 rounded-lg text-center">
                  <div className="text-4xl font-bold text-yellow-700 mb-2">
                    {summary.hold}
                  </div>
                  <div className="text-yellow-600 font-medium">
                    On Hold (
                    {summary.hold > 0
                      ? Math.round((summary.hold / summary.total) * 100)
                      : 0}
                    %)
                  </div>
                </div>

                <div className="bg-secondary-soft p-6 rounded-lg text-center">
                  <div className="text-4xl font-bold text-foreground mb-2">
                    {summary.accepted}
                  </div>
                  <div className="text-foreground font-medium">
                    Accepted (
                    {summary.accepted > 0
                      ? Math.round((summary.accepted / summary.total) * 100)
                      : 0}
                    %)
                  </div>
                </div>
              </div>

              {summary.hold > 0 && (
                <button
                  disabled={isSubmitting}
                  onClick={() => {
                    // Filter and review only hold applications
                    const holdApplications = applications.filter(
                      (app) => decisions[app.id] === "hold",
                    );
                    // console.log("sdaf,:", decisions, holdApplications)
                    setApplicationsState(holdApplications);
                    setCurrentIndex(0);
                  }}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <ClipboardList className="w-5 h-5" />
                  Review Hold Applications
                </button>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setApplicationsState(applications);
              submitApplications();
              // setCurrentIndex(0);
              // setDecisions({});
            }}
            className="w-full bg-[#7ecfae] hover:bg-primary-dark text-white py-3 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <RefreshCw className="w-5 h-5" />
            )}
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    );
  }
  if (!currentApplication && !isReviewComplete) {
    return (
      <div className="min-h-screen bg-muted p-4 flex flex-col items-center">
        {!isSkippedLockedBannerDismissed && (
          <SkippedLockedBanner
            info={skippedLockedInfo}
            onDismiss={() => setIsSkippedLockedBannerDismissed(true)}
          />
        )}
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="bg-card rounded-xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              No applications to review
            </h2>
            <p className="text-foreground">
              There are currently no applications available for review.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const removeReloadEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
    window.removeEventListener("beforeunload", handleBeforeUnload);
    window.location.reload();
  };

  return (
    <>
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="p-5 rounded-lg shadow-lg flex flex-col items-center">
            <Loader2 className="w-24 h-24 animate-spin" />
          </div>
        </div>
      )}
      <div className="min-h-screen bg-muted p-4 flex flex-col items-center">
        {!isSkippedLockedBannerDismissed && (
          <SkippedLockedBanner
            info={skippedLockedInfo}
            onDismiss={() => setIsSkippedLockedBannerDismissed(true)}
          />
        )}

        {/* Updated Filter Bar */}
        {Object.keys(getFilterOptions()).length > 0 && (
          <div className="w-full max-w-7xl mb-6">
            <div className="bg-card rounded-lg shadow-sm p-4">
              <div className="flex items-start  gap-5 justify-between">
                <div className="flex items-center mt-1 gap-2">
                  <Filter className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-medium text-foreground">Filters</h3>
                </div>

                <div className="flex items-center flex-wrap gap-4">
                  {Object.keys(filters).length > 0 && (
                    <button
                      onClick={handleResetFilters}
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground whitespace-nowrap"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                  {Object.entries(getFilterOptions()).map(([key, values]) => (
                    //  <div key={key} className="flex flex-col gap-1">
                    //  <label className="text-sm text-muted-foreground">
                    //    {getDisplayName(key)}:
                    //  </label>
                    <div key={key}>
                      <Dropdown
                        options={values.map((value) => ({
                          value,
                          label: value,
                        }))}
                        value={filters[key] || ""}
                        onChange={(selected) =>
                          handleFilterChange(key, selected.value)
                        }
                        placeholder={`All ${getDisplayName(key)}`}
                        searchable={true}
                        triggerClassName="border rounded-md px-3 py-1.5 text-sm min-w-[150px]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Existing Progress Counter */}
        {/* <div className="mb-4 bg-card px-4 py-2 rounded-full shadow-md">
          <span className="text-foreground">
            Application {currentIndex + 1} of {applicationsState.length}
          </span>
        </div> */}

        <div className="relative w-full max-w-7xl">
          <CurrentApplicationView
            direction={direction}
            currentApplication={currentApplication}
            currentVacancy={currentVacancy}
            tagsPerApplication={tagsPerApplication}
            availableTags={availableTags}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            showCV={showCV}
            onToggleCV={() => setShowCV(!showCV)}
            onScoreChange={handleScoreChange}
            onAddNote={handleAddNote}
            onEditNote={handleEditNote}
            onShowHistory={handleApplicationHistoryModalOpen}
            quickSaveChild={(arg) => <NotificationPopup {...arg} />}
            currentScores={scoresPerApplication[currentApplication?.id]}
            currentNotes={notesPerApplication[currentApplication?.id]}
          />

          <ThumbButtons
            onDecision={handleDecision}
            onUndo={handleUndo}
            canUndo={decisionHistory.length > 0 && currentIndex > 0}
          />
        </div>
      </div>

      <StatusHistoryDialog
        application={selectedPreviousApplication}
        onClose={handleApplicationHistoryModalClose}
      />

      {showNoMatchModal && (
        <NoMatchModal onClose={() => setShowNoMatchModal(false)} />
      )}

      {/* Add the notification popup component */}
      {/* <NotificationPopup /> */}

      {/* Add confirmation modal */}
      <CancelConfirmationModal />

      {/* <Dialoguepopup 
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        error={error}
        isBatchActive={applications?.batch?.is_active}
        onReload={() => {
          setShowErrorDialog(false);
          window.location.reload();
        }}
      /> */}

      <DialogBox
        isOpen={showErrorDialog}
        onClose={() => {
          window.close();
        }}
        title={
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
            <span className="text-xl font-semibold text-red-500">Error</span>
          </div>
        }
        actionButton={
          <>
            {hasUnsavedChanges && (
              <button
                onClick={() => {
                  // Save changes logic here
                  setHasUnsavedChanges(false);
                }}
                className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-md mr-2"
              >
                Save Changes
              </button>
            )}
            <button
              onClick={removeReloadEvent}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
            >
              Reload Page
            </button>
          </>
        }
      >
        <p className="text-muted-foreground">{errorMessage}</p>
      </DialogBox>
    </>
  );
}

// const Dialoguepopup = ({ isOpen, onClose, error, isBatchActive, onReload }) => {
//   const handleAction = () => {
//     onClose();
//     window.location.reload();
//     onReload();

//   };

//   const actionButton = (
//     <button
//       onClick={handleAction}
//       className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
//     >
//       Reload Page
//     </button>
//   );

//   return (
//     <DialogBox
//     isOpen={isOpen}
//     onClose={() => {
//       onClose();
//       window.close();
//     }}
//       title={
//         <div className="flex items-center">
//           <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
//           <span className="text-xl font-semibold text-red-500">Error</span>
//         </div>
//       }
//       actionButton={actionButton}
//     >
//       <p className="text-muted-foreground">
//         {!isBatchActive ? "Batch is not active. Please contact your administrator." : error || "An unexpected error occurred. Please try again."}
//       </p>
//     </DialogBox>
//   );
// };

const NoMatchModal = ({ onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div ref={modalRef} className="bg-card rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-semibold mb-4">No Applications Match</h2>
        <p className="text-muted-foreground mb-6">
          No applications match the selected filter criteria. The last filter
          has been removed.
        </p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="bg-[#7ecfae] hover:bg-primary-dark text-white px-4 py-2 rounded-md"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreScreenView;
